import { InsightCategoryChart } from './insight-category-chart';

/**
 * One Magic Insights answer field ready to render as a card. Skins build these from their profile
 * endpoints: `schema` and `value` are one property of a response's display_schema and answer, and
 * `displayType` picks the renderer in company-profile-insight-answer.
 *
 * The Logistics skin uses the platform display types (pills, card_list, gauge, ...). The Tax skin maps
 * them onto the compact types below so estimates, claims and projects read as formatted values.
 */
export interface CompanyProfileInsight {
  key: string;
  title: string;
  eyebrow?: string;
  schema: any;
  value: any;
  displayType: string;
  categoryChart?: InsightCategoryChart | null;
  response: any;
  /** Shown instead of the answer when the value is an empty list. */
  emptyText?: string;
  [extra: string]: any;
}

/** Compact renderers used by the Tax skin. Platform display types keep their existing renderers. */
export type CompanyProfileInsightCompactType =
  'likelihood' | 'amount' | 'records' | 'chips' | 'notes' | 'bars' | 'breakdown' | 'text';

/** Logistics skin defaults for the platform chart renderers. */
export const PROFILE_INSIGHT_CHART_COLOURS = ['#244a3b', '#4f7853', '#78984c', '#a5bd62', '#cedb8a', '#7a9f85', '#dbe9ac'];
export const PROFILE_INSIGHT_GAUGE_COLOUR = '#9eb84b';
export const PROFILE_INSIGHT_CARD_LIST_HEADER_COLOUR = '#365647';

export interface InsightRecord {
  lead: string;
  title: string;
  amount: string;
  body: string;
  meta: string[];
  flag: string;
}

export interface InsightBar {
  label: string;
  value: string;
  percent: number;
  empty: boolean;
}

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const NUMBER = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function hasInsightValue(value: any): boolean {
  return value !== null && value !== undefined && value !== '' &&
    (!Array.isArray(value) || value.length > 0) &&
    (typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length > 0);
}

export function insightValueText(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return NUMBER.format(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.slice(0, 3).map(item => insightValueText(item)).join(' · ');
  const fields = Object.entries(value).filter(([key, item]) =>
    key !== 'domain' && key !== 'country_code' && hasInsightValue(item)
  );
  return fields.slice(0, 2).map(([, item]) => insightValueText(item)).join(' · ') || 'View detail';
}

export function insightDetailRows(value: any, label: string): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  const visit = (item: any, path: string): void => {
    if (item === null || item === undefined) {
      rows.push({ label: path, value: 'Not available' });
    } else if (Array.isArray(item)) {
      if (!item.length) rows.push({ label: path, value: 'None supplied' });
      item.forEach((entry, index) => visit(entry, `${path} ${index + 1}`));
    } else if (typeof item === 'object') {
      const fields = Object.entries(item);
      if (!fields.length) rows.push({ label: path, value: 'No details supplied' });
      fields.forEach(([key, entry]) => visit(entry, `${path} · ${key.replace(/_/g, ' ')}`));
    } else {
      rows.push({ label: path, value: insightValueText(item) });
    }
  };
  visit(value, label);
  return rows;
}

export function insightListItems(value: any): any[] {
  return Array.isArray(value) ? value : [];
}

export function insightListText(item: any, schema: any): string {
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  if (!item || typeof item !== 'object') return '';
  const key = schema?.text_property_name || schema?.header_key_name;
  return String((key && item[key]) || Object.values(item).find(value => typeof value === 'string') || '');
}

export function insightListDetail(item: any, schema: any): string {
  if (!item || typeof item !== 'object') return '';
  const key = schema?.content_key_name || schema?.on_hover_property_name;
  const detail = key && item[key];
  return detail && detail !== insightListText(item, schema) ? String(detail) : '';
}

// ---------------------------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------------------------

export function insightConfidencePercent(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const percent = Number(value);
  return Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : null;
}

export function insightConfidenceLabel(value: unknown): string {
  const percent = insightConfidencePercent(value);
  return percent === null ? 'Confidence unavailable' : `${Math.round(percent)}% confidence`;
}

export function insightConfidenceColour(value: unknown): string {
  const percent = insightConfidencePercent(value);
  if (percent === null) return '#8b9b90';

  // Continuous stops: red through 50%, warm yellow in the middle, green at 80%+.
  const stops = [
    { percent: 0, colour: [165, 54, 53] },
    { percent: 50, colour: [212, 80, 66] },
    { percent: 65, colour: [198, 158, 42] },
    { percent: 80, colour: [78, 153, 89] },
    { percent: 100, colour: [36, 124, 77] },
  ];
  const upperIndex = stops.findIndex(stop => percent <= stop.percent);
  const upper = stops[upperIndex];
  const lower = stops[Math.max(0, upperIndex - 1)];
  const mix = upper === lower ? 0 : (percent - lower.percent) / (upper.percent - lower.percent);
  const channels = upper.colour.map((channel, index) =>
    Math.round(lower.colour[index] + (channel - lower.colour[index]) * mix)
      .toString(16).padStart(2, '0')
  );
  return `#${channels.join('')}`;
}

// ---------------------------------------------------------------------------------------------
// Compact formatting (Tax skin)
// ---------------------------------------------------------------------------------------------

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function formatInsightCurrency(value: unknown): string {
  const number = toNumber(value);
  return number === null ? '' : GBP.format(number);
}

/** Whole years arrive as 2023 or 2023.0; 0 means the model did not know. */
export function formatInsightYear(value: unknown): string {
  const number = toNumber(value);
  return number !== null && number >= 1900 && number <= 2200 ? String(Math.round(number)) : '';
}

/** ISO dates read as "31 Dec 2023"; a bare year stays a year. Unparseable text is returned as is. */
export function formatInsightDate(value: unknown, monthOnly = false): string {
  if (value === null || value === undefined || value === '') return '';
  const year = formatInsightYear(value);
  if (year) return year;
  if (typeof value === 'number') return '';
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(String(value));
  if (!match) return String(value);
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return String(value);
  return monthOnly || !match[3] ? `${month} ${match[1]}` : `${Number(match[3])} ${month} ${match[1]}`;
}

function isCurrencySchema(schema: any): boolean {
  return schema?.prepend === '£' || /gbp/i.test(String(schema?.label || ''));
}

/** Number answers: GBP as currency, units appended, energy in kWh also given in MWh or GWh. */
export function insightAmount(value: unknown, schema: any): { value: string; unit: string; note: string } {
  const number = toNumber(value);
  if (number === null) return { value: '—', unit: '', note: 'No figure supplied' };
  if (isCurrencySchema(schema)) {
    return { value: GBP.format(number), unit: '', note: number === 0 ? 'No value estimated' : '' };
  }
  const unit = String(schema?.append || '').trim();
  let note = '';
  if (/^kwh$/i.test(unit) && number >= 1000) {
    note = number >= 1e6 ? `About ${NUMBER.format(number / 1e6)} GWh a year` : `About ${NUMBER.format(number / 1e3)} MWh a year`;
  }
  return { value: new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(number), unit, note };
}

export function insightLikelihood(value: unknown, schema: any, key = ''): { percent: number; label: string; band: string } | null {
  const number = toNumber(value);
  if (number === null) return null;
  const maximum = toNumber(schema?.maximum_value) || 100;
  const percent = Math.min(100, Math.max(0, (number / maximum) * 100));
  const isLikelihood = /likelihood/i.test(key + ' ' + (schema?.label || ''));
  const band = !isLikelihood ? '' :
    percent >= 80 ? 'Very likely' : percent >= 60 ? 'Likely' : percent >= 40 ? 'Possible' :
    percent >= 20 ? 'Unlikely' : 'Very unlikely';
  return { percent, label: `${Math.round(percent)}%`, band };
}

const RECORD_META_LABELS: Record<string, string> = {
  year_commitment_made: 'Committed',
  target_year: 'Target',
  target_date: 'Target',
  year_installed: 'Installed',
  capacity_in_kwh: 'Capacity',
  renewal_date: 'Renews',
  energy_percentage: 'Share of energy use',
  years_active: 'Active',
};

const keyList = (value: unknown): string[] =>
  (Array.isArray(value) ? value : value ? [value] : []).filter((key): key is string => typeof key === 'string');

/** A card_list item as a tidy row: a year lead, a title, an optional amount and short meta facts. */
export function insightRecord(item: any, schema: any): InsightRecord {
  const record: InsightRecord = { lead: '', title: '', amount: '', body: '', meta: [], flag: '' };
  if (item === null || item === undefined) return record;
  if (typeof item !== 'object') {
    record.title = String(item);
    return record;
  }
  const headerKey = String(schema?.header_key_name || '');
  const contentKeys = keyList(schema?.content_key_name);
  const keys = contentKeys.length ? contentKeys : Object.keys(item).filter(key => key !== headerKey);
  const header = headerKey ? item[headerKey] : undefined;
  const yearHeader = /year/i.test(headerKey);
  if (yearHeader) record.lead = formatInsightYear(header);
  else if (header !== null && header !== undefined && header !== '') record.title = String(header);

  for (const key of keys) {
    const value = item[key];
    if (value === null || value === undefined || value === '') continue;
    const label = RECORD_META_LABELS[key] || '';
    if (key === 'domain') continue;
    if (key === 'country_code') { record.flag = String(value).toLowerCase(); continue; }
    if (/amount|_gbp|cost|fee/i.test(key) && toNumber(value) !== null) {
      if (toNumber(value)) record.amount = GBP.format(Number(value));
      else record.meta.push('Amount not disclosed');
      continue;
    }
    if (/kwh/i.test(key)) {
      const number = toNumber(value);
      if (number) record.meta.push(`${label || 'Capacity'} ${NUMBER.format(number)} kWh`);
      continue;
    }
    if (/percentage/i.test(key)) {
      const number = toNumber(value);
      if (number !== null) record.meta.push(`${label} ${NUMBER.format(number)}%`.trim());
      continue;
    }
    if (/year|date/i.test(key) && key !== 'years_active') {
      const text = formatInsightDate(value);
      if (text) record.meta.push(label ? `${label} ${text}` : text);
      continue;
    }
    const text = String(value).trim();
    if (!text) continue;
    if (!record.title) record.title = text;
    else if (text.length > 60 && !record.body) record.body = text;
    else record.meta.push(label ? `${label} ${text}` : text);
  }
  if (!record.title) record.title = record.amount ? 'Claim recorded' : 'Detail not supplied';
  return record;
}

/** card_list answers sorted newest first when each item is keyed by year. */
export function insightRecords(value: any, schema: any): InsightRecord[] {
  const records = insightListItems(value).map(item => insightRecord(item, schema));
  if (/year/i.test(String(schema?.header_key_name || ''))) {
    return records.map((record, index) => ({ record, index }))
      .sort((a, b) => (Number(b.record.lead) || 0) - (Number(a.record.lead) || 0) || a.index - b.index)
      .map(entry => entry.record);
  }
  return records;
}

/** labelled_pills detail: years and dates read naturally, percentages get a sign. */
export function insightNoteDetail(item: any, schema: any): string {
  const key = schema?.on_hover_property_name;
  if (!key || !item || typeof item !== 'object') return '';
  const value = item[key];
  if (value === null || value === undefined || value === '') return '';
  if (/percent|share|proportion/i.test(key) && toNumber(value) !== null) return `${NUMBER.format(Number(value))}%`;
  if (/year|date/i.test(key)) return formatInsightDate(value, true);
  return String(value);
}

/** vertical_bar_chart arrays as labelled horizontal bars. Fees and costs are shown in GBP. */
export function insightBars(value: any, schema: any, key = ''): InsightBar[] {
  const values = insightListItems(value).map(item => toNumber(item));
  const labels: string[] = Array.isArray(schema?.axis_labels) ? schema.axis_labels : [];
  const currency = isCurrencySchema(schema) || /fee|cost|amount|gbp/i.test(key + ' ' + (schema?.label || ''));
  const maximum = Math.max(0, ...values.map(item => item || 0));
  return values.map((item, index) => ({
    label: labels[index] || `Item ${index + 1}`,
    value: !item ? 'Not reported' : currency ? GBP.format(item) : NUMBER.format(item),
    percent: item && maximum ? Math.max(2, (item / maximum) * 100) : 0,
    empty: !item,
  }));
}

/** pie_chart arrays as a share-of-total breakdown. */
export function insightBreakdown(value: any, schema: any): InsightBar[] {
  const labelKey = schema?.label_name;
  const valueKey = schema?.value_name;
  const rows = insightListItems(value).map(item => ({
    label: String(item?.[labelKey] ?? ''),
    amount: toNumber(item?.[valueKey]) || 0,
  })).filter(row => row.label);
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  return rows.sort((a, b) => b.amount - a.amount).map(row => ({
    label: row.label,
    value: [
      schema?.prepend === '£' ? GBP.format(row.amount) : NUMBER.format(row.amount) + (schema?.append ? ` ${schema.append}` : ''),
      total ? `${Math.round((row.amount / total) * 100)}%` : '',
    ].filter(Boolean).join(' · '),
    percent: total ? Math.max(2, (row.amount / total) * 100) : 0,
    empty: !row.amount,
  }));
}

const SOURCE_LABELS: Record<string, string> = {
  accounts: 'Company accounts',
  website: 'Company website',
  news: 'News article',
  self_published_news: 'Company news',
  linkedin: 'LinkedIn',
  jobs: 'Job listing',
  hiring: 'Job listing',
  patents: 'Patent record',
  grants: 'Grant record',
  companies_house: 'Companies House',
};

export function insightSourceLabel(source: unknown): string {
  const text = String(source || '').trim();
  if (!text) return 'Source';
  return SOURCE_LABELS[text.toLowerCase()] || text.charAt(0).toUpperCase() + text.slice(1).replace(/_/g, ' ');
}
